import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  message,
  Popconfirm,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  BankOutlined,
  WalletOutlined,
  UserOutlined,
  PhoneOutlined,
  SendOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import {
  fetchWithdrawRecords,
  approveWithdraw,
  processWithdraw,
  setFilters,
  setPagination,
  clearError,
} from '../../features/finance/financeSlice';
import { formatDateTime, formatAmount, formatAmountWithRegion, maskPhone, getStatusColor, getStatusText } from '../../utils/index';
import { get } from '../../utils/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const WithdrawManagement = () => {
  const dispatch = useDispatch();
  const { withdrawRecords, pagination, filters, isLoading, error } = useSelector((state) => state.finance);
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [processModalVisible, setProcessModalVisible] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // 使用 useMemo 计算请求参数
  const requestParams = React.useMemo(() => ({
    ...filters,
    page: pagination.current || 1,
    limit: pagination.pageSize || 20,
  }), [filters, pagination.current, pagination.pageSize]);
  
  // 使用 useRef 防止死循环
  const prevParamsRef = React.useRef('');
  const isLoadingRef = React.useRef(false);
  
  useEffect(() => {
    // 如果正在加载，则不重复请求
    if (isLoadingRef.current) {
      return;
    }
    
    // 深度比较参数，只有真正变化时才请求
    const paramsStr = JSON.stringify(requestParams);
    
    if (paramsStr !== prevParamsRef.current) {
      prevParamsRef.current = paramsStr;
      isLoadingRef.current = true;
      dispatch(fetchWithdrawRecords(requestParams)).finally(() => {
        isLoadingRef.current = false;
      });
    }
  }, [dispatch, requestParams]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 获取地区列表
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await get('/admin/regions/all');
        if (res.code === 200) {
          const regionList = res.data || [];
          setRegions(regionList);
          // 默认选择中国地区（CN）
          const defaultRegion = regionList.find(r => 
            (r.region_code || r.regionCode) === 'ZMW'
          );
          if (defaultRegion) {
            setSelectedRegion(defaultRegion);
          }
        }
      } catch (error) {
        console.error('获取地区列表失败:', error);
      }
    };
    fetchRegions();
  }, []);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
  };

  // 表格列配置
  const columns = [
    {
      title: '提现ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户信息',
      dataIndex: 'userName',
      key: 'userName',
      render: (name, record) => (
        <Space>
          <UserOutlined />
          <span>{record.userPhone}</span>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
            ({name})
          </span>
        </Space>
      ),
    },
    {
      title: '提现金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Space direction="vertical" size={0}>
          <Space>
            <DollarOutlined />
            <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
              {selectedRegion ? formatAmountWithRegion(amount, selectedRegion) : formatAmount(amount)}
            </span>
          </Space>
          {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              原始: {formatAmount(amount)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: '手续费',
      dataIndex: 'fee',
      key: 'fee',
      render: (fee) => (
        <Space direction="vertical" size={0}>
          <span style={{ color: '#8c8c8c' }}>
            {selectedRegion ? formatAmountWithRegion(fee, selectedRegion) : formatAmount(fee)}
          </span>
          {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              原始: {formatAmount(fee)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: '实际到账',
      dataIndex: 'actualAmount',
      key: 'actualAmount',
      render: (amount) => (
        <Space direction="vertical" size={0}>
          <Space>
            <BankOutlined />
            <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {selectedRegion ? formatAmountWithRegion(amount, selectedRegion) : formatAmount(amount)}
            </span>
          </Space>
          {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              原始: {formatAmount(amount)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: '提现方式',
      dataIndex: 'withdrawMethod',
      key: 'withdrawMethod',
      render: (method) => {
        const methodMap = {
          bank: { text: '银行卡', color: 'blue' },
          alipay: { text: '支付宝', color: 'green' },
          wechat: { text: '微信', color: 'green' },
          mpesa: { text: 'M-pesa', color: 'green' },
        };
        const config = methodMap[method] || { text: method, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '提现状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDateTime(date),
    },
    {
      title: '到账时间',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (date) => date ? formatDateTime(date) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="通过">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => handleApprove(record, true)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="拒绝">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => handleApprove(record, false)}
                  danger
                />
              </Tooltip>
            </>
          )}
          {record.status === 'approved' && (
            <Tooltip title="处理">
              <Button
                type="text"
                icon={<SendOutlined />}
                onClick={() => handleProcess(record)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // 查看详情
  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  // 审核提现
  const handleApprove = (record, approved) => {
    setSelectedRecord(record);
    setApproveModalVisible(true);
  };

  // 处理提现
  const handleProcess = (record) => {
    setSelectedRecord(record);
    setProcessModalVisible(true);
  };

  // 确认审核
  const handleConfirmApprove = async (values) => {
    try {
      await dispatch(approveWithdraw({
        recordId: selectedRecord.id,
        approved: values.approved,
        reason: values.reason,
      })).unwrap();
      message.success(values.approved ? '提现审核通过' : '提现审核拒绝');
      setApproveModalVisible(false);
      dispatch(fetchWithdrawRecords({ ...filters, ...pagination }));
    } catch (error) {
      message.error(error);
    }
  };

  // 确认处理
  const handleConfirmProcess = async (values) => {
    try {
      await dispatch(processWithdraw({
        recordId: selectedRecord.id,
        transactionId: values.transactionId,
      })).unwrap();
      message.success('提现处理成功');
      setProcessModalVisible(false);
      dispatch(fetchWithdrawRecords({ ...filters, ...pagination }));
    } catch (error) {
      message.error(error);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">提现管理</h1>
        <p className="page-description">管理用户提现申请和处理</p>
      </div>

      <Card>
        {/* 地区选择器 */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '8px', display: 'none' }}>
          <GlobalOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
          <span style={{ fontWeight: 500 }}>显示货币：</span>
          <Select
            style={{ width: 360 }}
            value={selectedRegion ? (selectedRegion.region_code || selectedRegion.regionCode) : undefined}
            onChange={(value) => {
              const region = regions.find(r => 
                (r.region_code || r.regionCode) === value
              );
              setSelectedRegion(region);
            }}
            placeholder="选择地区"
          >
            {regions.map(region => {
              const regionCode = region.region_code || region.regionCode;
              const regionName = region.region_name || region.regionName;
              const currencySymbol = region.currency_symbol || region.currencySymbol;
              const currencyName = region.currency_name || region.currencyName;
              const exchangeRate = region.exchange_rate || region.exchangeRate || 1;
              
              return (
                <Option key={regionCode} value={regionCode}>
                  <Space>
                    <Tag color="cyan">{regionCode}</Tag>
                    <span>{regionName}</span>
                    <Tag color="green">{currencySymbol} {currencyName}</Tag>
                    {exchangeRate !== 1 && (
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        (汇率: {exchangeRate})
                      </span>
                    )}
                  </Space>
                </Option>
              );
            })}
          </Select>
          {selectedRegion && (
            <Tag color="blue">
              当前: {selectedRegion.currency_symbol || selectedRegion.currencySymbol} {selectedRegion.currency_name || selectedRegion.currencyName}
            </Tag>
          )}
        </div>

        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              placeholder="搜索用户手机号/姓名"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="提现状态" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
              <Option value="processing">处理中</Option>
              <Option value="completed">已完成</Option>
            </Select>
          </Form.Item>
          <Form.Item name="withdrawMethod">
            <Select placeholder="提现方式" style={{ width: 120 }} allowClear>
              <Option value="bank">银行卡</Option>
              <Option value="alipay">支付宝</Option>
              <Option value="wechat">微信</Option>
              <Option value="mpesa">mpesa</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Space>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              icon={<CheckOutlined />}
            >
              批量通过
            </Button>
            <Button
              danger
              disabled={selectedRowKeys.length === 0}
              icon={<CloseOutlined />}
            >
              批量拒绝
            </Button>
          </Space>
          <div>
            共 {pagination.total} 条提现记录
          </div>
        </div>

        {/* 提现记录表格 */}
        <Table
          columns={columns}
          dataSource={withdrawRecords}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 提现详情抽屉 */}
      <Drawer
        title="提现详情"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Statistic
                  title="提现金额"
                  value={selectedRecord.amount}
                  prefix={<DollarOutlined />}
                  formatter={(value) => selectedRegion ? formatAmountWithRegion(value, selectedRegion) : formatAmount(value)}
                />
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    原始: {formatAmount(selectedRecord.amount)}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Statistic
                  title="实际到账"
                  value={selectedRecord.actualAmount}
                  prefix={<BankOutlined />}
                  formatter={(value) => selectedRegion ? formatAmountWithRegion(value, selectedRegion) : formatAmount(value)}
                />
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    原始: {formatAmount(selectedRecord.actualAmount)}
                  </div>
                )}
              </Col>
            </Row>

            <Descriptions
              title="提现信息"
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="提现编号">
                {selectedRecord.withdrawNo || selectedRecord.id}
              </Descriptions.Item>
              <Descriptions.Item label="用户信息">
                {selectedRecord.userName} ({maskPhone(selectedRecord.userPhone)})
              </Descriptions.Item>
              <Descriptions.Item label="提现金额">
                <Space direction="vertical" size={0}>
                  <span>{selectedRegion ? formatAmountWithRegion(selectedRecord.amount, selectedRegion) : formatAmount(selectedRecord.amount)}</span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.amount)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="手续费">
                <Space direction="vertical" size={0}>
                  <span>{selectedRegion ? formatAmountWithRegion(selectedRecord.fee, selectedRegion) : formatAmount(selectedRecord.fee)}</span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.fee)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="实际到账">
                <Space direction="vertical" size={0}>
                  <span>{selectedRegion ? formatAmountWithRegion(selectedRecord.actualAmount, selectedRegion) : formatAmount(selectedRecord.actualAmount)}</span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.actualAmount)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="提现方式">
                <Tag color="blue">{selectedRecord.withdrawMethodName || selectedRecord.withdrawMethod || '-'}</Tag>
              </Descriptions.Item>
              {/* 根据提现方式显示不同的信息 */}
              {selectedRecord.withdrawInfo && (
                <>
                  {selectedRecord.withdrawInfo.realName && (
                    <Descriptions.Item label="真实姓名">
                      {selectedRecord.withdrawInfo.realName}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.bankName && (
                    <Descriptions.Item label="银行名称">
                      {selectedRecord.withdrawInfo.bankName}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.bankAccount && (
                    <Descriptions.Item label="银行账号">
                      {maskPhone(selectedRecord.withdrawInfo.bankAccount)}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.branchName && (
                    <Descriptions.Item label="开户支行">
                      {selectedRecord.withdrawInfo.branchName}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.alipayAccount && (
                    <Descriptions.Item label="支付宝账号">
                      {maskPhone(selectedRecord.withdrawInfo.alipayAccount)}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.wechatAccount && (
                    <Descriptions.Item label="微信号">
                      {selectedRecord.withdrawInfo.wechatAccount}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.usdtAddress && (
                    <Descriptions.Item label="USDT地址">
                      {selectedRecord.withdrawInfo.usdtAddress}
                    </Descriptions.Item>
                  )}
                  {selectedRecord.withdrawInfo.usdtNetwork && (
                    <Descriptions.Item label="USDT网络">
                      {selectedRecord.withdrawInfo.usdtNetwork}
                    </Descriptions.Item>
                  )}
                </>
              )}
              {/* 兼容旧数据格式 */}
              {!selectedRecord.withdrawInfo && (
                <>
                  <Descriptions.Item label="银行卡号">
                    {selectedRecord.bankCardNumber || selectedRecord.bankAccount || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="开户行">
                    {selectedRecord.bankName || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="开户人">
                    {selectedRecord.accountHolder || selectedRecord.accountName || '-'}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="提现状态">
                <Tag color={getStatusColor(selectedRecord.status)}>
                  {getStatusText(selectedRecord.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="审核状态">
                <Tag color={selectedRecord.auditStatus === 'approved' ? 'green' : selectedRecord.auditStatus === 'rejected' ? 'red' : 'orange'}>
                  {selectedRecord.auditStatus === 'approved' ? '已通过' : selectedRecord.auditStatus === 'rejected' ? '已拒绝' : '待审核'}
                </Tag>
              </Descriptions.Item>
              {selectedRecord.auditRemark && (
                <Descriptions.Item label="审核备注">
                  {selectedRecord.auditRemark}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="申请时间">
                {formatDateTime(selectedRecord.createdAt)}
              </Descriptions.Item>
              {selectedRecord.processedAt && (
                <Descriptions.Item label="处理时间">
                  {formatDateTime(selectedRecord.processedAt)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="完成时间">
                {selectedRecord.completedAt ? formatDateTime(selectedRecord.completedAt) : '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      {/* 审核模态框 */}
      <Modal
        title="审核提现"
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmApprove}
        >
          {/* 用户详情信息 */}
          {selectedRecord?.userDetails && (
            <Card 
              title="用户详情" 
              size="small" 
              style={{ marginBottom: 16, backgroundColor: '#fafafa' }}
            >
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>用户姓名</div>
                  <div style={{ fontWeight: 500 }}>{selectedRecord?.userName}</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>手机号</div>
                  <div style={{ fontWeight: 500 }}>{maskPhone(selectedRecord?.userPhone)}</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>用户余额</div>
                  <div style={{ fontWeight: 500, color: '#52c41a' }}>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.userDetails.balance, selectedRegion) : selectedRecord.userDetails.balance}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>冻结余额</div>
                  <div style={{ fontWeight: 500, color: '#ff4d4f' }}>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.userDetails.frozenBalance, selectedRegion) : selectedRecord.userDetails.frozenBalance}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>可用余额</div>
                  <div style={{ fontWeight: 500, color: '#1890ff' }}>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.userDetails.availableBalance, selectedRegion) : selectedRecord.userDetails.availableBalance}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>用户等级</div>
                  <div style={{ fontWeight: 500 }}>
                    <Tag color="blue">LV{selectedRecord.userDetails.userLevel}</Tag>
                    {selectedRecord.userDetails.levelName}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>累计充值</div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.userDetails.totalRecharge, selectedRegion) : selectedRecord.userDetails.totalRecharge}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>累计提现</div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.userDetails.totalWithdraw, selectedRegion) : selectedRecord.userDetails.totalWithdraw}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>账户状态</div>
                  <div style={{ fontWeight: 500 }}>
                    <Tag color={selectedRecord.userDetails.accountStatus === 1 ? 'green' : 'red'}>
                      {selectedRecord.userDetails.accountStatus === 1 ? '正常' : '已禁用'}
                    </Tag>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>注册时间</div>
                  <div style={{ fontWeight: 500 }}>{formatDateTime(selectedRecord.userDetails.registerTime)}</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>最后登录时间</div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRecord.userDetails.lastLoginTime ? formatDateTime(selectedRecord.userDetails.lastLoginTime) : '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '12px', color: '#666' }}>最后登录IP</div>
                  <div style={{ fontWeight: 500 }}>{selectedRecord.userDetails.lastLoginIp || '-'}</div>
                </Col>
              </Row>
            </Card>
          )}

          {/* 提现信息 */}
          <Card title="提现信息" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>提现金额</div>
                <div style={{ fontWeight: 500, color: '#ff4d4f', fontSize: '16px' }}>
                  {selectedRegion ? formatAmountWithRegion(selectedRecord?.amount, selectedRegion) : formatAmount(selectedRecord?.amount)}
                </div>
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    原始: {formatAmount(selectedRecord?.amount)}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>手续费</div>
                <div style={{ fontWeight: 500, fontSize: '16px' }}>
                  {selectedRegion ? formatAmountWithRegion(selectedRecord?.fee, selectedRegion) : formatAmount(selectedRecord?.fee)}
                </div>
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    原始: {formatAmount(selectedRecord?.fee)}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>实际到账</div>
                <div style={{ fontWeight: 500, color: '#52c41a', fontSize: '16px' }}>
                  {selectedRegion ? formatAmountWithRegion(selectedRecord?.actualAmount, selectedRegion) : formatAmount(selectedRecord?.actualAmount)}
                </div>
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    原始: {formatAmount(selectedRecord?.actualAmount)}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: '12px', color: '#666' }}>提现方式</div>
                <div style={{ fontWeight: 500 }}>
                  <Tag color="blue">{selectedRecord?.withdrawMethodName || selectedRecord?.withdrawMethod}</Tag>
                </div>
              </Col>
            </Row>
          </Card>

          {/* 审核操作 */}
          <Form.Item
            label="审核结果"
            name="approved"
            rules={[{ required: true, message: '请选择审核结果' }]}
          >
            <Select placeholder="请选择审核结果">
              <Option value={true}>通过</Option>
              <Option value={false}>拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="审核备注"
            name="reason"
            rules={[{ required: true, message: '请输入审核备注' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入审核备注"
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认审核
              </Button>
              <Button onClick={() => setApproveModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 处理模态框 */}
      <Modal
        title="处理提现"
        open={processModalVisible}
        onCancel={() => setProcessModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmProcess}
        >
          <Form.Item label="用户信息">
            <Input value={`${selectedRecord?.userName} (${maskPhone(selectedRecord?.userPhone)})`} disabled />
          </Form.Item>
          <Form.Item label="提现金额">
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Input 
                value={selectedRegion ? formatAmountWithRegion(selectedRecord?.amount, selectedRegion) : formatAmount(selectedRecord?.amount)} 
                disabled 
              />
              {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                <span style={{ fontSize: '12px', color: '#999' }}>
                  原始金额: {formatAmount(selectedRecord?.amount)}
                </span>
              )}
            </Space>
          </Form.Item>
          <Form.Item label="实际到账">
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Input 
                value={selectedRegion ? formatAmountWithRegion(selectedRecord?.actualAmount, selectedRegion) : formatAmount(selectedRecord?.actualAmount)} 
                disabled 
              />
              {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                <span style={{ fontSize: '12px', color: '#999' }}>
                  原始金额: {formatAmount(selectedRecord?.actualAmount)}
                </span>
              )}
            </Space>
          </Form.Item>
          <Form.Item label="提现方式">
            <Input value={selectedRecord?.withdrawMethod} disabled />
          </Form.Item>
          <Form.Item
            label="交易流水号"
            name="transactionId"
            rules={[{ required: true, message: '请输入交易流水号' }]}
          >
            <Input placeholder="请输入交易流水号" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认处理
              </Button>
              <Button onClick={() => setProcessModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WithdrawManagement;
